// App state (signals) and actions. UI components read these; App.tsx syncs them to the Viewer and the URL.
import { batch, computed, signal } from '@preact/signals';
import { track } from '../analytics';
import { LAYERS, PEEL_ORDER, initialLayers, type LayerId, type LayerState } from '../viewer/layers';
import type { CameraState, IsolateMode, Side } from '../viewer/Viewer';
import { BY_ID, STRUCTURES } from './structures';
import { flags } from './flags';

export interface Isolation { mode: IsolateMode; ids: string[]; region?: string }
interface Snapshot { layers: Record<LayerId, LayerState>; hidden: string[]; isolate: Isolation | null }

export const layers = signal<Record<LayerId, LayerState>>(initialLayers());
export const hidden = signal<string[]>([]);
export const isolate = signal<Isolation | null>(null);
export const selected = signal<{ id: string; side?: Side } | null>(null);
export const camera = signal<CameraState | null>(null);
export const loadedLayers = signal<Set<LayerId>>(new Set());
export const loadProgress = signal(0);
export const sheet = signal<'peek' | 'half' | 'full'>('half');
export const panel = signal<'card' | 'browse' | 'tours' | 'unlock' | null>(null);
export const exportOpen = signal(false);
// ---------- body (F22) ----------
export type BodyChoice = 'm' | 'f';
const BODY_KEY = 'marrow.body';
/** The remembered body preference (a preference, not an identifier). */
export function preferredBody(): BodyChoice {
  if (!flags.femaleBody) return 'm';
  try {
    return localStorage.getItem(BODY_KEY) === 'f' ? 'f' : 'm';
  } catch {
    return 'm';
  }
}
export const body = signal<BodyChoice>('m');
/** Shows the other body. Remembers the choice, and clears a selection that doesn't exist in that body. */
export function setBody(b: BodyChoice, opts: { remember?: boolean } = {}) {
  if (body.value === b || (b === 'f' && !flags.femaleBody)) return;
  batch(() => {
    body.value = b;
    const sel = selected.value && BY_ID.get(selected.value.id);
    if (sel?.body && sel.body !== b) selected.value = null;
    hidden.value = hidden.value.filter((id) => !BY_ID.get(id)?.body || BY_ID.get(id)?.body === b);
  });
  if (opts.remember !== false) {
    try {
      localStorage.setItem(BODY_KEY, b);
    } catch {
      /* storage blocked: the choice lasts for this visit */
    }
  }
}
/** Layers with parts in each figure (F22): the female figure has no muscles, for example. */
const LAYERS_IN: Record<BodyChoice, Set<LayerId>> = {
  m: new Set(STRUCTURES.filter((s) => s.body !== 'f').map((s) => s.layer)),
  f: new Set(STRUCTURES.filter((s) => s.body !== 'm').map((s) => s.layer)),
};
/** Whether the figure on show has any parts in a layer. */
export const layerInBody = (id: LayerId, b: BodyChoice = body.value) => LAYERS_IN[b].has(id);
export const offlineOpen = signal(false);
/** Set when a new app version ('app') or newer offline content ('content') is ready (F12). */
export const updateReady = signal<null | 'app' | 'content'>(null);
export const online = signal(typeof navigator === 'undefined' ? true : navigator.onLine);
export const toast = signal<{ text: string; id: number } | null>(null);
export const classroom = signal(false);
export const showSensitive = signal(true);
export const layersOpen = signal(typeof innerWidth === 'number' ? innerWidth >= 1024 : true);
/** When true, selecting a part fades the layers that hide it (false for direct clicks). */
export const revealOnSelect = signal(true);

const past: Snapshot[] = [];
const future: Snapshot[] = [];
export const canUndo = signal(false);
export const canRedo = signal(false);

const snap = (): Snapshot => ({ layers: { ...layers.value }, hidden: [...hidden.value], isolate: isolate.value ? { ...isolate.value, ids: [...isolate.value.ids] } : null });
function restore(s: Snapshot) {
  batch(() => {
    layers.value = s.layers;
    hidden.value = s.hidden;
    isolate.value = s.isolate;
  });
}
/** Records the current visual state so the next change can be undone (≥20 steps kept). */
function record() {
  past.push(snap());
  if (past.length > 50) past.shift();
  future.length = 0;
  canUndo.value = true;
  canRedo.value = false;
}
export function undo() {
  const s = past.pop();
  if (!s) return;
  future.push(snap());
  restore(s);
  canUndo.value = past.length > 0;
  canRedo.value = true;
}
export function redo() {
  const s = future.pop();
  if (!s) return;
  past.push(snap());
  restore(s);
  canRedo.value = future.length > 0;
  canUndo.value = true;
}
export function clearHistory() {
  past.length = 0;
  future.length = 0;
  canUndo.value = canRedo.value = false;
}

export function showToast(text: string) {
  toast.value = { text, id: Date.now() };
}

// ---------- layers (F02) ----------
export function setLayer(id: LayerId, state: LayerState) {
  if (layers.value[id] === state) return;
  record();
  layers.value = { ...layers.value, [id]: state };
}
export function cycleLayer(id: LayerId) {
  const next: Record<LayerState, LayerState> = { on: 'ghost', ghost: 'off', off: 'on' };
  setLayer(id, next[layers.value[id]]);
}
/** Steps outside-in: each press turns the outermost still-visible layer off. After the last, shows all. */
export function peel() {
  record();
  const cur = layers.value;
  const outer = PEEL_ORDER.find((l) => cur[l] !== 'off');
  if (!outer || PEEL_ORDER.every((l) => cur[l] === 'off')) {
    layers.value = Object.fromEntries(LAYERS.map((l) => [l.id, l.id === 'skin' ? 'ghost' : l.id === 'ligaments' ? 'off' : 'on'])) as Record<LayerId, LayerState>;
    return;
  }
  layers.value = { ...cur, [outer]: 'off', skeletal: cur.skeletal === 'off' ? 'on' : cur.skeletal };
}
export const peelDone = computed(() => PEEL_ORDER.every((l) => layers.value[l] === 'off'));

/** Ghosts the given layers if they are currently on (undoable). */
export function ghostLayers(ids: LayerId[]) {
  const next = { ...layers.value };
  let changed = false;
  for (const id of ids) if (next[id] === 'on') {
    next[id] = 'ghost';
    changed = true;
  }
  if (!changed) return;
  record();
  layers.value = next;
}

// ---------- selection / isolation (F03) ----------
export function select(id: string | null, side?: Side) {
  if (!id) {
    selected.value = null;
    return;
  }
  const s = BY_ID.get(id);
  if (!s || (s.body === 'f' && !flags.femaleBody)) return;
  // A part that exists in only one body brings that body into view (F22: search "womb" from the male body).
  if (s.body && s.body !== body.value) {
    setBody(s.body);
    showToast(s.body === 'f' ? 'Showing the female body' : 'Showing the male body');
  }
  batch(() => {
    // Selecting something in a hidden layer turns that layer on (keeps other states).
    if (layers.value[s.layer] === 'off' || layers.value[s.layer] === 'ghost') layers.value = { ...layers.value, [s.layer]: 'on' };
    if (hidden.value.includes(id)) hidden.value = hidden.value.filter((h) => h !== id);
    if (isolate.value && !isolate.value.ids.includes(id) && !(isolate.value.mode === 'region' && isolate.value.region === s.region)) isolate.value = null;
    selected.value = { id, side };
    if (typeof innerWidth === 'number' && innerWidth < 1024) layersOpen.value = false;
    panel.value = 'card';
  });
}
export function isolateSelected(mode: IsolateMode) {
  const sel = selected.value;
  if (!sel) return;
  record();
  const s = BY_ID.get(sel.id);
  isolate.value = { mode, ids: [sel.id], region: mode === 'region' ? s?.region : undefined };
}
export function hideSelected() {
  const sel = selected.value;
  if (!sel) return;
  record();
  batch(() => {
    hidden.value = [...new Set([...hidden.value, sel.id])];
    selected.value = null;
  });
}
export function hideId(id: string) {
  record();
  hidden.value = [...new Set([...hidden.value, id])];
}
export function showAll() {
  if (!hidden.value.length && !isolate.value) return;
  record();
  batch(() => {
    hidden.value = [];
    isolate.value = null;
  });
}

// ---------- classroom (F15) ----------
/** Classroom mode lasts for the browser session (a preference, not an identifier). */
const CLASSROOM_KEY = 'marrow.classroom';
export function toggleClassroom(on = !classroom.value) {
  batch(() => {
    classroom.value = on;
    showSensitive.value = !on; // sensitive parts start hidden; the teacher can show them
  });
  document.documentElement.toggleAttribute('data-classroom', on);
  track('classroom_mode', { state: on ? 'on' : 'off' });
  try {
    if (on) sessionStorage.setItem(CLASSROOM_KEY, '1');
    else sessionStorage.removeItem(CLASSROOM_KEY);
  } catch {
    /* storage blocked: the mode lasts for this page only */
  }
}
/** `?classroom=1` (or an earlier toggle in this session) turns classroom mode on at start-up. */
export function classroomRequested(search = location.search): boolean {
  const p = new URLSearchParams(search).get('classroom');
  if (p === '0') return false;
  if (p === '1') return true;
  try {
    return sessionStorage.getItem(CLASSROOM_KEY) === '1';
  } catch {
    return false;
  }
}
/** Hidden in classroom mode unless the teacher has chosen to show sensitive parts. */
export function sensitiveHidden(): boolean {
  return classroom.value && !showSensitive.value;
}

// ---------- tours (F11) ----------
export const tour = signal<{ id: string; stop: number } | null>(null);
/** Structures highlighted by a tour stop (tint + outline, several at once). */
export const highlight = signal<string[]>([]);
